import styles from './ai-lyrics-alternative.module.css'

import { IoIosCheckmarkCircleOutline } from '@react-icons/all-files/io/IoIosCheckmarkCircleOutline'
import { IoIosCloseCircleOutline } from '@react-icons/all-files/io/IoIosCloseCircleOutline'
import { RxMagicWand } from '@react-icons/all-files/rx/RxMagicWand'
import React, { useState } from 'react'

import type {
  AiLyricsAlternative as AiLyricsAlternativeType,
  AiLyricsAlternativeStatus,
} from 'types/track'

import {
  AccessibleIcon,
  Button,
  Callout,
  Card,
  Flex,
  IconButton,
  Text,
  TextArea,
} from 'components/ds'
import { ConfidenceBadge } from 'components/ds/AiSuggestTextArea'

import type { DiffPart } from './lyrics-diff'
import { changedLyricLines } from './lyrics-diff'

type Props = {
  alternative: AiLyricsAlternativeType
  currentLyrics: string
  readOnly?: boolean
  onAccept: () => void
  onReject: () => void
  onClose: () => void
}

type CalloutStatus = Exclude<AiLyricsAlternativeStatus, 'rejected'>

const calloutText: Record<CalloutStatus, string> = {
  pending: 'An AI version of these lyrics is ready.',
  accepted: 'AI version kept as an alternative.',
}

/**
 * Tells the user about the AI lyrics and opens them beside the current lyrics.
 */
export const AiLyricsAlternativeCallout = ({
  status,
  onOpen,
}: {
  status: CalloutStatus
  onOpen: () => void
}) => (
  <Callout.Root color="sky" variant="soft" size="1" className={styles.callout}>
    <Flex align="center" justify="between" gap="3" width="100%">
      <Flex align="center" gap="2">
        <RxMagicWand />
        <Text size="2">{calloutText[status]}</Text>
      </Flex>
      <Button size="1" color="gray" highContrast onClick={onOpen}>
        {status === 'pending' ? 'Review' : 'View'}
      </Button>
    </Flex>
  </Callout.Root>
)

const DiffText = ({ part }: { part: DiffPart }) => {
  switch (part.kind) {
    case 'same':
      return <span>{part.text}</span>
    case 'removed':
      return (
        <>
          <Text color="red">
            <s>{part.text}</s>
          </Text>{' '}
        </>
      )
    case 'added':
      return (
        <Text color="green" weight="bold">
          {part.text}
        </Text>
      )
    default:
      return part.kind satisfies never
  }
}

/**
 * AI lyrics shown beside a track's current lyrics, as changed lines by default.
 * Accepting keeps the AI version as an alternative; current lyrics stay primary.
 */
const AiLyricsAlternative = ({
  alternative,
  currentLyrics,
  readOnly = false,
  onAccept,
  onReject,
  onClose,
}: Props) => {
  const [showFull, setShowFull] = useState(false)
  const rows = changedLyricLines(currentLyrics, alternative.value)
  const confidence = alternative.confidence
    ? parseFloat(alternative.confidence)
    : undefined

  return (
    <Flex direction="column" gap="1">
      <Flex align="center" justify="between" gap="2" minHeight="24px">
        <Flex align="center" gap="2">
          <Text size="1" color="gray">
            AI version
          </Text>
          {confidence !== undefined && (
            <ConfidenceBadge confidence={confidence} />
          )}
        </Flex>
        <Flex align="center" gap="2">
          {alternative.status === 'pending' && !readOnly && (
            <>
              <IconButton type="button" size="1" onClick={onAccept}>
                <AccessibleIcon label="Keep as alternative">
                  <IoIosCheckmarkCircleOutline />
                </AccessibleIcon>
              </IconButton>
              <IconButton type="button" size="1" color="red" onClick={onReject}>
                <AccessibleIcon label="Reject">
                  <IoIosCloseCircleOutline />
                </AccessibleIcon>
              </IconButton>
            </>
          )}
          <Button size="1" variant="ghost" color="gray" onClick={onClose}>
            Close
          </Button>
        </Flex>
      </Flex>
      {showFull ? (
        <TextArea
          aria-label="AI version"
          rows={12}
          value={alternative.value}
          readOnly
        />
      ) : (
        <Card size="1">
          <Flex direction="column" gap="2">
            {rows.map((row, rowIndex) => (
              <Text key={rowIndex} as="p" size="2">
                {row.map((part, partIndex) => (
                  <DiffText key={partIndex} part={part} />
                ))}
              </Text>
            ))}
          </Flex>
        </Card>
      )}
      <Flex justify="between" align="center">
        <Text size="1" color="gray">
          {showFull
            ? 'Full AI version'
            : `${rows.length} ${
                rows.length === 1 ? 'line differs' : 'lines differ'
              } from your lyrics`}
        </Text>
        <Button size="1" variant="ghost" onClick={() => setShowFull(!showFull)}>
          {showFull ? 'Show changes only' : 'Show full AI version'}
        </Button>
      </Flex>
    </Flex>
  )
}

export default AiLyricsAlternative
